import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { ROLES } from "../../../utils";
import { useCourse, useUserAdvance } from "../../../hooks";
import { CourseProgram, CourseProgress } from "./components";
import { Error, CourseItemModal, Loader } from "../../../components";
import {
  closeModalSlideshow,
  closeModalVideo,
} from "../../../store/slices";

import "../../../assets/css/modern-course.css";

export const Course = () => {
  const [showProgram, setShowProgram] = useState(false);

  const user = useSelector((state) => state.user);

  const {
    isOpen: isModalVideoOpen,
    videoId,
  } = useSelector((state) => state.app.modalVideo);

  const {
    isOpen: isModalSlideshowOpen,
    slideId,
  } = useSelector((state) => state.app.modalSlideshow);

  const {
    loading,
    error,
    course,
    courseAdvance,
  } = useCourse({
    user,
    isProtected: true,
  });

  const {
    error: errorAdvance,
  } = useUserAdvance({ user });

  const dispatch = useDispatch();

  useEffect(() => {
    if (!showProgram) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showProgram]);

  if (loading) {
    return <Loader delay={0} />;
  }

  if (error || errorAdvance) {
    return <Error />;
  }

  const moduleCount = course?.modules?.length || 0;

  return (
    <>
      <div className="modern-course-page">
        <div className="modern-course-shell">

          <div className="modern-course-breadcrumb">
            <Link to="/">
              <i className="fas fa-arrow-left" />
              Mis cursos
            </Link>

            <span>
              {moduleCount} {moduleCount === 1 ? "módulo" : "módulos"}
            </span>
          </div>

          <section className="modern-course-hero anim" style={{ "--delay": "0s" }}>
            <div className="modern-course-hero-copy">
              <span className="modern-course-eyebrow">
                MI CAPACITACIÓN
              </span>

              <h1>{course?.title}</h1>

              <p>
                Continúa con tu capacitación, revisa tu avance y accede al
                contenido del curso desde un solo lugar.
              </p>
            </div>

            <div className="modern-course-hero-mark">
              <div className="modern-course-hero-icon">
                <i className="fas fa-graduation-cap" />
              </div>

              <div>
                <strong>Curso en línea</strong>
                <span>Proceso Seguro</span>
              </div>
            </div>
          </section>

          {user.roleId !== ROLES.admin && (
            <CourseProgress
              user={user}
              course={course}
              courseAdvance={courseAdvance}
            />
          )}

          {course?.program_url && (
            <section className="modern-course-section">
              <div className="modern-course-section-heading">
                <div>
                  <span>DOCUMENTACIÓN</span>
                  <h2>Programa del curso</h2>
                  <p>
                    Consulta el programa general sin salir de la plataforma.
                  </p>
                </div>
              </div>

              <div className="modern-program-card anim" style={{ "--delay": ".2s" }}>
                <div className="modern-program-card-info">
                  <div className="modern-program-icon">
                    <i className="fas fa-file-pdf" />
                  </div>

                  <div>
                    <strong>Programa del curso</strong>
                    <span>
                      Documento oficial con el contenido y estructura del curso.
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  className="modern-program-button"
                  onClick={() => setShowProgram(true)}
                >
                  <i className="fas fa-eye" />
                  Ver programa
                </button>
              </div>
            </section>
          )}

          <section className="modern-course-section modern-course-content-section">
            <div className="modern-course-section-heading">
              <div>
                <span>APRENDIZAJE</span>
                <h2>Contenido del curso</h2>
                <p>
                  Avanza módulo por módulo y completa las actividades disponibles.
                </p>
              </div>

              <div className="modern-module-count">
                <strong>{moduleCount}</strong>
                <span>{moduleCount === 1 ? "módulo" : "módulos"}</span>
              </div>
            </div>

            <CourseProgram course={course} />
          </section>

        </div>
      </div>

      {showProgram && course?.program_url && (
        <div
          className="program-viewer-overlay"
          onClick={() => setShowProgram(false)}
        >
          <div
            className="program-viewer-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="program-viewer-body">
              <iframe
                src={`${course.program_url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
                title="Programa del curso"
                className="program-viewer-frame"
              />
            </div>

            <div className="program-viewer-footer">
              <button
                type="button"
                className="program-viewer-close-btn"
                onClick={() => setShowProgram(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <CourseItemModal
        slideID={videoId}
        show={isModalVideoOpen}
        onHide={() => dispatch(closeModalVideo())}
      />

      <CourseItemModal
        slideID={slideId}
        show={isModalSlideshowOpen}
        onHide={() => dispatch(closeModalSlideshow())}
      />
    </>
  );
};
